import { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, MailCheck, Users } from "lucide-react";
import { useMutation } from "@apollo/client/react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScioMark } from "@/components/ui/scio-mark";
import { PublicHeader } from "@/components/layout/public-header";
import { REGISTER_WITH_EMAIL } from "@/graphql/mutations/account";
import { TermsConsent } from "@/components/legal/terms-consent";

/**
 * One form, two kinds of account, two ways to sign up.
 *
 * Everything here is a convenience for the person filling it in — the rules are
 * enforced on the server, because the Google half of this form is one editable
 * URL: sign-in leaves carrying `role` and `birth_date` as query parameters and
 * they come back on `omniauth.params`. "admin" is refused there
 * (`RegistrationIntent`), a learner with no usable birth date is refused there,
 * and `registerWithEmail` re-checks the identical rules for the other half.
 *
 * **Google is still the primary and it is not merely habit.** Google has already
 * verified the address, so that account arrives confirmed and can be used at
 * once; an address typed here is asserted, so it costs a confirmation email and
 * cannot be signed in with until the link is followed. The form says so before
 * anybody types a password, rather than after.
 */

type Role = "guardian" | "student";

/** What `registerWithEmail` answers. `emailed` is false when the account exists
 *  but the mail did not leave — which the page has to say rather than swallow. */
type RegisterResult = {
  registerWithEmail: { success: boolean; emailed: boolean; errors: string[] };
};

/** Mirrors RegistrationIntent::MIN_AGE / MAX_AGE, which is what actually decides. */
const MIN_AGE = 3;
const MAX_AGE = 120;

function ageOn(birthDate: string): number | null {
  const born = new Date(birthDate);
  if (Number.isNaN(born.getTime())) return null;

  const today = new Date();
  let years = today.getFullYear() - born.getFullYear();
  const beforeBirthday =
    today.getMonth() < born.getMonth() ||
    (today.getMonth() === born.getMonth() && today.getDate() < born.getDate());
  if (beforeBirthday) years -= 1;
  return years;
}

export default function RegisterPage() {
  const [role, setRole] = useState<Role | null>(null);
  const [birthDate, setBirthDate] = useState("");
  // One checkbox for both ways in, because both create an account and both are
  // refused on the server without it (issue #40).
  const [agreed, setAgreed] = useState(false);
  const [withEmail, setWithEmail] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  // Set once the account exists. The page then stops being a form: there is
  // nothing left to fill in and the next step is in their inbox, so offering the
  // fields again would invite a second account on a second address.
  const [sent, setSent] = useState<{ emailed: boolean } | null>(null);

  const [register, { loading }] = useMutation<RegisterResult>(REGISTER_WITH_EMAIL, {
    onCompleted: (data) => {
      const result = data.registerWithEmail;
      if (result.success) setSent({ emailed: result.emailed });
      else setErrors(result.errors);
    },
    // A network failure is not a rejected registration, and saying „patikrinkite
    // duomenis" would send them to correct a form that was fine.
    onError: () => setErrors(["Nepavyko susisiekti su serveriu. Bandykite dar kartą."]),
  });

  const age = birthDate ? ageOn(birthDate) : null;
  const birthDateProblem =
    role === "student" && birthDate && (age === null || age < MIN_AGE || age > MAX_AGE)
      ? "Patikrinkite gimimo datą."
      : null;

  const ready =
    agreed && (role === "guardian" || (role === "student" && !!birthDate && !birthDateProblem));

  /** Mirrors Student::MIN_PASSWORD_LENGTH, which is what actually decides. */
  const MIN_PASSWORD = 8;
  const emailReady =
    ready && name.trim().length > 0 && email.includes("@") && password.length >= MIN_PASSWORD;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    register({
      variables: {
        name: name.trim(),
        email: email.trim(),
        password,
        role,
        // Sent only for a learner: a grown-up's age is of no use to this app and
        // is not kept. The server ignores it for a guardian either way.
        birthDate: role === "student" ? birthDate : null,
        acceptsTerms: agreed,
      },
    });
  };

  const href = () => {
    // `terms=1` rides the Google round trip like `role` does; without it the
    // callback creates nothing (RegistrationIntent#valid?).
    const params = new URLSearchParams({ role: role ?? "", terms: agreed ? "1" : "" });
    if (role === "student") params.set("birth_date", birthDate);
    return `/users/auth/google_oauth2?${params.toString()}`;
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader action="register" />

        <div className="flex items-center justify-center px-4 pb-16 pt-4 sm:pt-10">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <MailCheck className="mx-auto mb-2 h-11 w-11 text-primary" aria-hidden="true" />
              <CardTitle className="text-center text-2xl font-bold tracking-tight">
                Patikrinkite el. paštą
              </CardTitle>
              <CardDescription className="text-center">
                Nuorodą išsiuntėme adresu <strong>{email.trim()}</strong>. Paspauskite ją —
                ir galėsite prisijungti.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* The account exists whether or not the mail left, so a failure to
                  send has to be said out loud. Silence here would leave somebody
                  waiting for a link that was never sent, with nothing to press. */}
              {!sent.emailed && (
                <Alert variant="destructive">
                  <AlertDescription>
                    Laiško išsiųsti nepavyko. Paskyra sukurta — nuorodos galite paprašyti
                    dar kartą prisijungimo lange.
                  </AlertDescription>
                </Alert>
              )}

              <p className="text-sm text-muted-foreground">
                Laiško nėra? Pažiūrėkite šlamšto (spam) dėžutėje — o jei ir ten nėra,
                prisijungimo lange galėsite paprašyti naujos nuorodos.
              </p>

              <Button asChild className="w-full">
                <Link to="/auth/login">Eiti į prisijungimą</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader action="register" />

      <div className="flex items-center justify-center px-4 pb-16 pt-4 sm:pt-10">
        <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <ScioMark size={44} className="mx-auto mb-2 text-primary" title="Scio" />
          <CardTitle className="text-center text-2xl font-bold tracking-tight">
            Sukurkite paskyrą
          </CardTitle>
          <CardDescription className="text-center">
            Kas naudosis Scio? Tai galima pakeisti tik susisiekus, tad pasirinkite tiksliai.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <RoleCard
              icon={Users}
              title="Tėvas ar mokytojas"
              body="Pridėsiu mokinius ir matysiu jų pažangą."
              selected={role === "guardian"}
              onSelect={() => setRole("guardian")}
            />
            <RoleCard
              icon={GraduationCap}
              title="Mokinys"
              body="Spręsiu užduotis pats."
              selected={role === "student"}
              onSelect={() => setRole("student")}
            />
          </div>

          {role === "student" && (
            <div className="space-y-2">
              <Label htmlFor="birth-date">Gimimo data</Label>
              <Input
                id="birth-date"
                type="date"
                value={birthDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setBirthDate(e.target.value)}
                aria-invalid={birthDateProblem ? true : undefined}
              />
              <p className="text-xs text-muted-foreground">
                Pagal ją parenkame klasę ir pirmąsias užduotis.
              </p>
              {birthDateProblem && (
                <Alert variant="destructive">
                  <AlertDescription>{birthDateProblem}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Above both buttons, because it governs both. */}
          {role && <TermsConsent checked={agreed} onChange={setAgreed} />}

          {/* A full page navigation, not a fetch: this hands the browser to
              Google and comes back at /users/auth/google_oauth2/callback.
              `disabled` is not a thing an <a> has — and `asChild` renders the
              anchor — so the unready state is styled and blocked explicitly
              rather than left looking pressable. */}
          <Button
            asChild
            className={`w-full ${ready ? "" : "pointer-events-none opacity-50"}`}
          >
            <a
              href={ready ? href() : undefined}
              aria-disabled={!ready}
              tabIndex={ready ? undefined : -1}
              onClick={(e) => {
                if (!ready) e.preventDefault();
              }}
            >
              Registruotis su Google
            </a>
          </Button>

          {!role && (
            <p className="text-center text-xs text-muted-foreground">
              Pirma pasirinkite, kas naudosis.
            </p>
          )}
          {role && !agreed && (
            <p className="text-center text-xs text-muted-foreground">
              Pažymėkite, kad sutinkate su taisyklėmis.
            </p>
          )}

          {/* The other way in. Folded away rather than shown beside Google: two
              equal-looking options make somebody choose before they have any way
              to tell them apart, and Google is the one that works immediately. */}
          {!withEmail ? (
            <button
              type="button"
              onClick={() => setWithEmail(true)}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
            >
              Registruotis su el. paštu ir slaptažodžiu
            </button>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4 border-t pt-4">
              {errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {errors.map((message) => (
                      <span key={message} className="block">
                        {message}
                      </span>
                    ))}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Vardas</Label>
                <Input
                  id="name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">El. paštas</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="vardas@pastas.lt"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Slaptažodis</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  minLength={MIN_PASSWORD}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Bent {MIN_PASSWORD} simboliai.
                </p>
              </div>

              {/* Said before the password is typed, not after: it is the one way
                  this differs from the Google button above, and finding out
                  afterwards reads as the account being broken. */}
              <p className="text-xs text-muted-foreground">
                Į šį adresą išsiųsime patvirtinimo nuorodą. Kol jos nepaspausite,
                prisijungti su slaptažodžiu negalėsite.
              </p>

              <Button type="submit" className="w-full" disabled={loading || !emailReady}>
                {loading ? "Kuriama..." : "Sukurti paskyrą"}
              </Button>
            </form>
          )}

          <p className="text-center text-sm text-muted-foreground">
            Jau turite paskyrą?{" "}
            <Link to="/auth/login" className="underline underline-offset-4">
              Prisijungti
            </Link>
          </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RoleCard({
  icon: Icon,
  title,
  body,
  selected,
  onSelect,
}: {
  icon: typeof Users;
  title: string;
  body: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`rounded-xl border p-4 text-left transition-all active:scale-[0.98] ${
        selected ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:border-primary/40"
      }`}
    >
      <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
      <span className="mt-2 block font-semibold">{title}</span>
      <span className="mt-0.5 block text-xs text-muted-foreground">{body}</span>
    </button>
  );
}
